// AdvertisementManager.js - Landscaping Only Version

import React, { useState, useEffect } from 'react';
import {
    Plus,
    Edit2,
    Trash2,
    Megaphone,
    Save,
    X,
    Tag,
    ChevronRight,
    Star,
    Activity,
    Eye,
    Home,
    TreePine,
    Flower2,
    Building2,
    MapPin
} from 'lucide-react';
import Toast from './Toast';

const API_BASE = '';

// ==================== HELPERS ====================
const getIconComponent = (iconName) => {
    switch(iconName) {
        case 'Home': return <Home size={24} />;
        case 'TreePine': return <TreePine size={24} />;
        case 'Flower2': return <Flower2 size={24} />;
        case 'Building': return <Building2 size={24} />;
        case 'SwimmingPool': return <div style={{ fontSize: 24 }}>🏊</div>;
        case 'Umbrella': return <div style={{ fontSize: 24 }}>🏖️</div>;
        default: return <Tag size={24} />;
    }
};
const getFullImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath === '/' || imagePath.trim() === '') return null;

    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath;
    }

    let cleanPath = imagePath;
    if (cleanPath.startsWith('/nursery/')) {
        cleanPath = cleanPath.replace('/nursery', '');
    }

    if (cleanPath.startsWith('/uploads/')) {
        return `${window.location.origin}${cleanPath}`;
    }
    if (cleanPath.startsWith('uploads/')) {
        return `${window.location.origin}/${cleanPath}`;
    }

    if (!cleanPath.startsWith('/') && !cleanPath.startsWith('http')) {
        return `${window.location.origin}/uploads/${cleanPath}`;
    }

    return `${window.location.origin}/uploads/${cleanPath}`;
};
const defaultLandscapingOffers = [
    { 
        id: 101, 
        title: 'Complete Garden Makeover', 
        description: 'Full landscape design and installation for your villa or commercial space.', 
        features: ['3D Design Consultation', 'Hardscaping & Softscaping', 'Irrigation System Installation', '2 Months Maintenance'], 
        color: '#2D7D46', 
        active: true, 
        campaign_type: 'landscaping',
        icon: 'Home',
        price: '12,499',
        location: 'Dubai, UAE',
        image: '',
        beforeImage: ''
    },
    { 
        id: 102, 
        title: 'Poolside Paradise', 
        description: 'Transform your pool area into a tropical oasis with premium landscaping.', 
        features: ['Tropical Plant Selection', 'Stone Pathways', 'Outdoor Lounge Area', 'Smart Lighting'], 
        color: '#00695C', 
        active: true, 
        campaign_type: 'landscaping',
        icon: 'SwimmingPool',
        price: '8,999',
        location: 'Abu Dhabi, UAE',
        image: '',
        beforeImage: ''
    },
    { 
        id: 103, 
        title: 'Rooftop Retreat', 
        description: 'Convert your rooftop into a stunning green sanctuary with panoramic views.', 
        features: ['Custom Planters', 'Irrigation System', 'Furniture Styling', 'Privacy Screening'], 
        color: '#4E342E', 
        active: true, 
        campaign_type: 'landscaping',
        icon: 'Umbrella',
        price: '6,499',
        location: 'Sharjah, UAE',
        image: '',
        beforeImage: ''
    },
    { 
        id: 104, 
        title: 'Commercial Landscape Package', 
        description: 'Professional landscaping for offices, hotels, and retail spaces.', 
        features: ['Site Assessment', 'Design & Planning', 'Installation', '6 Months Maintenance'], 
        color: '#1565C0', 
        active: true, 
        campaign_type: 'landscaping',
        icon: 'Building',
        price: '19,999',
        location: 'Dubai, UAE',
        image: '',
        beforeImage: ''
    },
];

// ==================== SUB-COMPONENTS ====================

// 1. Header Component
const Header = ({ onAddOffer }) => {
    return (
        <div style={{
            background: '#f1f5f9',
            color: '#334155',
            padding: '12px 24px',
            borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px',
        }}>
            <div>
                <h1 className="h5 fw-bold mb-0 d-flex align-items-center gap-2">
                    <div className="p-2 bg-white rounded-3 border-secondary border-opacity-10 shadow-sm text-secondary">
                        <Megaphone size={18} />
                    </div>
                    Landscaping Campaigns
                </h1>
                <p className="mb-0 mt-1 text-muted small" style={{ fontSize: '11px' }}>
                    Develop and deploy exclusive landscaping offers to your storefront
                </p>
            </div>

            <button
                className="btn rounded-pill border-0 px-4 py-2 fw-bold text-white d-flex align-items-center gap-2"
                style={{
                    background: 'linear-gradient(135deg, #c9a03d 0%, #b8860b 100%)',
                    boxShadow: '0 8px 20px -6px rgba(201, 160, 61, 0.6)',
                    height: '44px'
                }}
                onClick={onAddOffer}
            >
                <Plus size={20} /> New Campaign
            </button>
        </div>
    );
};

// 2. Campaign Card Component
const CampaignCard = ({ offer, onEdit, onDelete, onToggle }) => {
    const imageSrc = offer.image ? getFullImageUrl(offer.image) : null;
    const beforeImageSrc = offer.beforeImage ? getFullImageUrl(offer.beforeImage) : null;

    return (
        <div className="card border-0 shadow-sm rounded-4 overflow-hidden transition-all hover-translate" 
             style={{ 
                 transition: 'all 0.3s ease',
                 height: '100%',
                 minHeight: '420px',
                 display: 'flex',
                 flexDirection: 'column'
             }}>
            <div style={{ height: '6px', background: offer.color || '#1B4D3E', flexShrink: 0 }} />
            
            {imageSrc && (
                <div className="position-relative" style={{ height: '180px', background: '#f0f0f0', flexShrink: 0 }}>
                    <img 
                        src={imageSrc}
                        alt={offer.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                            console.error('Image failed to load:', imageSrc);
                            e.target.style.display = 'none';
                            const parent = e.target.parentElement;
                            parent.innerHTML = `
                                <div style="display:flex;align-items:center;justify-content:center;height:100%;background:#f8f9fa;color:#6c757d;font-size:14px;">
                                    No Image Available
                                </div>
                            `;
                        }}
                    />
                    {offer.beforeImage && (
                        <div className="position-absolute top-0 end-0 m-2">
                            <span className="badge bg-warning text-dark" style={{ fontSize: '0.6rem' }}>
                                BEFORE/AFTER
                            </span>
                        </div>
                    )}
                </div>
            )}

            <div className="card-body p-4 d-flex flex-column" style={{ flex: 1 }}>
                <div className="d-flex justify-content-between align-items-start mb-3" style={{ flexShrink: 0 }}>
                    <div className="p-2 rounded-3" style={{ background: `${offer.color}10`, color: offer.color }}>
                        {getIconComponent(offer.icon)}
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        <span className={`badge rounded-pill px-3 py-1 ${offer.active ? 'bg-success bg-opacity-10 text-success border border-success border-opacity-25' : 'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25'}`}>
                            {offer.active ? 'LIVE' : 'DRAFT'}
                        </span>
                        <div className="form-check form-switch p-0 m-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                style={{ cursor: 'pointer', width: '2.5em', height: '1.25em' }}
                                checked={offer.active}
                                onChange={() => onToggle(offer.id)}
                            />
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-start mb-2" style={{ flexShrink: 0 }}>
                    <h5 className="fw-bold mb-0 text-dark" style={{ letterSpacing: '-0.02em', fontSize: '0.95rem' }}>
                        {offer.title}
                    </h5>
                    {offer.price && (
                        <span className="badge bg-dark bg-opacity-10 text-dark" style={{ flexShrink: 0 }}>
                             {offer.price}
                        </span>
                    )}
                </div>

                <p className="text-muted small mb-3" style={{ 
                    lineHeight: '1.5', 
                    fontSize: '0.8rem',
                    height: '42px',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    flexShrink: 0
                }}>
                    {offer.description}
                </p>

                <div style={{ 
                    flex: 1,
                    minHeight: '80px',
                    maxHeight: '120px',
                    overflow: 'hidden',
                    marginBottom: '12px'
                }}>
                    <p className="fw-bold small mb-2 text-uppercase text-muted border-bottom pb-1" style={{ 
                        fontSize: '9px', 
                        letterSpacing: '0.05em',
                        flexShrink: 0 
                    }}>
                        Service Features
                    </p>
                    <div className="d-flex flex-column gap-1">
                        {offer.features && offer.features.slice(0, 4).map((f, i) => f && (
                            <div key={i} className="d-flex align-items-center gap-2 small text-dark" style={{ fontSize: '0.75rem' }}>
                                <div className="rounded-circle" style={{ width: '5px', height: '5px', backgroundColor: offer.color, flexShrink: 0 }}></div>
                                <span style={{ 
                                    whiteSpace: 'nowrap', 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis' 
                                }}>
                                    {f}
                                </span>
                            </div>
                        ))}
                        {offer.features && offer.features.length > 4 && (
                            <div className="text-muted small" style={{ fontSize: '0.7rem' }}>
                                +{offer.features.length - 4} more
                            </div>
                        )}
                    </div>
                </div>

                <div className="d-flex gap-2 mt-auto pt-2 border-top" style={{ flexShrink: 0 }}>
                    <button
                        className="btn btn-sm btn-light border flex-grow-1 rounded-pill py-2 d-flex align-items-center justify-content-center gap-1 fw-bold text-dark"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => onEdit(offer)}
                    >
                        <Edit2 size={14} /> Configure
                    </button>
                    <button
                        className="btn btn-sm btn-outline-danger p-2 rounded-circle d-flex align-items-center justify-content-center border-0 bg-danger bg-opacity-10"
                        style={{ width: '38px', height: '38px', flexShrink: 0 }}
                        onClick={() => onDelete(offer.id)}
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// 3. Empty State Component
const EmptyState = ({ onAdd }) => {
    return (
        <div className="text-center py-5 bg-white rounded-4 shadow-sm">
            <div className="p-4 bg-light rounded-circle d-inline-flex mb-4">
                <Megaphone size={60} className="text-muted opacity-25" />
            </div>
            <h4 className="text-dark fw-bold">No Landscaping Campaigns</h4>
            <p className="text-muted small mb-4">
                You haven't launched any landscaping campaigns yet.
                <br />Start now to boost your landscaping services!
            </p>
            <button 
                className="btn btn-primary rounded-pill px-5 py-2 fw-bold shadow-sm" 
                style={{ backgroundColor: '#1b4d3e', borderColor: '#1b4d3e' }} 
                onClick={onAdd}
            >
                Launch First Campaign
            </button>
        </div>
    );
};

// 4. Campaign List Component
const CampaignList = ({ offers, onEdit, onDelete, onToggle, onAdd }) => {
    return (
        <div className="row g-4 px-3">
            <div className="col-12 mb-2">
                <div className="d-flex align-items-center gap-2 text-muted px-2">
                    <Activity size={18} className="text-success" />
                    <span className="fw-bold small text-uppercase tracking-wider">
                        Landscaping Campaigns
                    </span>
                    <div className="flex-grow-1 border-bottom ms-2 opacity-10"></div>
                </div>
            </div>
            
            {offers.length === 0 ? (
                <EmptyState onAdd={onAdd} />
            ) : (
                offers.map((offer) => (
                    <div key={offer.id} className="col-md-6 col-lg-4">
                        <CampaignCard
                            offer={offer}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onToggle={onToggle}
                        />
                    </div>
                ))
            )}
        </div>
    );
};

// 5. Campaign Preview Component
const CampaignPreview = ({ currentOffer }) => {
    const imageSrc = currentOffer?.image ? getFullImageUrl(currentOffer.image) : null;
    
    return (
        <div className="card border-0 shadow-lg rounded-4 p-4 bg-white w-100">
            <h6 className="fw-bold text-muted small text-uppercase tracking-wider mb-3" style={{ fontSize: '0.6rem' }}>
                <Eye size={14} className="me-1" /> Live Preview
            </h6>
            <div className="border rounded-3 p-3" style={{ background: '#fafafa' }}>
                <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="p-2 rounded-3" style={{ background: `${currentOffer?.color || '#2D7D46'}20`, color: currentOffer?.color || '#2D7D46' }}>
                        {getIconComponent(currentOffer?.icon)}
                    </div>
                    <div>
                        <h6 className="fw-bold mb-0" style={{ fontSize: '0.85rem' }}>{currentOffer?.title || 'Campaign Title'}</h6>
                        <p className="text-muted small mb-0" style={{ fontSize: '0.7rem' }}>{currentOffer?.location || 'Location'}</p>
                    </div>
                </div>
                
                {imageSrc && (
                    <img 
                        src={imageSrc} 
                        alt="Preview" 
                        style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px' }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                )}
                
                <p className="text-muted small mt-2 mb-0" style={{ fontSize: '0.7rem' }}>
                    {currentOffer?.description || 'Description will appear here'}
                </p>
                
                {currentOffer?.price && (
                    <div className="mt-2">
                        <span className="badge bg-dark">AED {currentOffer.price}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

// 6. Campaign Form Component
const CampaignForm = ({ currentOffer, setCurrentOffer, onSave, onCancel }) => {
    const [imagePreview, setImagePreview] = useState(null);
    const [beforeImagePreview, setBeforeImagePreview] = useState(null);

    useEffect(() => {
        if (currentOffer?.image) {
            const imageUrl = getFullImageUrl(currentOffer.image);
            setImagePreview(imageUrl);
        } else {
            setImagePreview(null);
        }

        if (currentOffer?.beforeImage) {
            const beforeUrl = getFullImageUrl(currentOffer.beforeImage);
            setBeforeImagePreview(beforeUrl);
        } else {
            setBeforeImagePreview(null);
        }
    }, [currentOffer]);

    const handleFeatureChange = (index, value) => {
        const newFeatures = [...(currentOffer.features || [])];
        newFeatures[index] = value;
        setCurrentOffer({ ...currentOffer, features: newFeatures });
    };

    const handleImageChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (type === 'main') {
                    setImagePreview(reader.result);
                    setCurrentOffer({ ...currentOffer, imageFile: file });
                } else if (type === 'before') {
                    setBeforeImagePreview(reader.result);
                    setCurrentOffer({ ...currentOffer, beforeImageFile: file });
                }
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <div className="row g-4 px-3">
            <div className="col-lg-7 d-flex">
                <div className="card border-0 shadow-lg rounded-4 p-4 animate-slide-up bg-white w-100 d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-shrink-0">
                        <div>
                            <h5 className="fw-bold mb-0 text-dark" style={{ fontSize: '1.1rem' }}>
                                {currentOffer?.id ? 'Refine Campaign' : 'New Landscaping Offer'}
                            </h5>
                            <p className="text-muted small mb-0 mt-1" style={{ fontSize: '0.75rem' }}>
                                Design a landscaping service package
                            </p>
                        </div>
                        <button className="btn btn-light rounded-circle shadow-sm p-1" style={{ width: '32px', height: '32px' }} onClick={onCancel}>
                            <X size={16} />
                        </button>
                    </div>

                    <form onSubmit={onSave} className="d-flex flex-column flex-grow-1">
                        <div className="row g-2 flex-grow-1">
                            <div className="col-12">
                                <label className="form-label text-muted small fw-bold text-uppercase tracking-wider" style={{ fontSize: '0.6rem' }}>
                                    Service Name
                                </label>
                                <input
                                    type="text"
                                    className="form-control border-0 bg-light rounded-4 ps-3"
                                    style={{ height: '38px', fontSize: '0.85rem', fontWeight: '600' }}
                                    placeholder="e.g., Villa Garden Transformation"
                                    value={currentOffer?.title || ''}
                                    onChange={e => setCurrentOffer({ ...currentOffer, title: e.target.value })}
                                    required
                                />
                            </div>
                            
                            <div className="col-12">
                                <label className="form-label text-muted small fw-bold text-uppercase tracking-wider" style={{ fontSize: '0.6rem' }}>
                                    Service Description
                                </label>
                                <textarea
                                    className="form-control border-0 bg-light rounded-4 ps-3 pt-2"
                                    rows="2"
                                    style={{ fontSize: '0.8rem' }}
                                    placeholder="Describe the landscaping service package..."
                                    value={currentOffer?.description || ''}
                                    onChange={e => setCurrentOffer({ ...currentOffer, description: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="col-12">
                                <label className="form-label text-muted small fw-bold text-uppercase tracking-wider" style={{ fontSize: '0.6rem' }}>
                                    Location
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text border-0 bg-light rounded-start-4 ps-3">
                                        <MapPin size={14} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-0 bg-light rounded-end-4 ps-2"
                                        style={{ height: '36px', fontSize: '0.8rem' }}
                                        placeholder="e.g., Dubai Hills Estate, Dubai"
                                        value={currentOffer?.location || ''}
                                        onChange={e => setCurrentOffer({ ...currentOffer, location: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="col-12">
                                <label className="form-label text-muted small fw-bold text-uppercase tracking-wider" style={{ fontSize: '0.6rem' }}>
                                    Campaign Image <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="file"
                                    id="campaignImage"
                                    className="form-control border-0 bg-light rounded-4"
                                    style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                                    accept="image/*"
                                    onChange={(e) => handleImageChange(e, 'main')}
                                />
                                {imagePreview && (
                                    <div className="mt-2 d-flex align-items-center gap-3">
                                        <img 
                                            src={imagePreview} 
                                            alt="Campaign preview" 
                                            style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #e2e8f0' }} 
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                        <span className="small text-muted" style={{ fontSize: '0.7rem' }}>
                                            {currentOffer?.imageFile ? 'New image selected' : 'Current image'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="col-12">
                                <label className="form-label text-muted small fw-bold text-uppercase tracking-wider" style={{ fontSize: '0.6rem' }}>
                                    Before Image (Optional)
                                </label>
                                <input
                                    type="file"
                                    id="beforeImage"
                                    className="form-control border-0 bg-light rounded-4"
                                    style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                                    accept="image/*"
                                    onChange={(e) => handleImageChange(e, 'before')}
                                />
                                {beforeImagePreview && (
                                    <div className="mt-2 d-flex align-items-center gap-3">
                                        <img 
                                            src={beforeImagePreview} 
                                            alt="Before preview" 
                                            style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #e2e8f0' }} 
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                        <span className="small text-muted" style={{ fontSize: '0.7rem' }}>
                                            {currentOffer?.beforeImageFile ? 'New before image selected' : 'Current before image'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="col-md-6">
                                <label className="form-label text-muted small fw-bold text-uppercase tracking-wider" style={{ fontSize: '0.6rem' }}>Color</label>
                                <div className="d-flex align-items-center gap-3 bg-light p-1.5 rounded-4">
                                    <input
                                        type="color"
                                        className="form-control form-control-color border-0 bg-transparent"
                                        style={{ width: '32px', height: '28px' }}
                                        value={currentOffer?.color || '#2D7D46'}
                                        onChange={e => setCurrentOffer({ ...currentOffer, color: e.target.value })}
                                    />
                                    <span className="small text-muted font-monospace" style={{ fontSize: '0.7rem' }}>{currentOffer?.color || '#2D7D46'}</span>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label text-muted small fw-bold text-uppercase tracking-wider" style={{ fontSize: '0.6rem' }}>Price</label>
                                <input
                                    type="text"
                                    className="form-control border-0 bg-light rounded-4 ps-3"
                                    style={{ height: '36px', fontSize: '0.8rem' }}
                                    placeholder="e.g., 12,699"
                                    value={currentOffer?.price || ''}
                                    onChange={e => setCurrentOffer({ ...currentOffer, price: e.target.value })}
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label text-muted small fw-bold text-uppercase tracking-wider" style={{ fontSize: '0.6rem' }}>Icon</label>
                                <select
                                    className="form-select border-0 bg-light rounded-4 ps-3"
                                    style={{ height: '36px', fontSize: '0.8rem' }}
                                    value={currentOffer?.icon || 'Home'}
                                    onChange={e => setCurrentOffer({ ...currentOffer, icon: e.target.value })}
                                >
                                    <option value="Home">🏠 Home</option>
                                    <option value="TreePine">🌲 Tree</option>
                                    <option value="Flower2">🌸 Flower</option>
                                    <option value="Building">🏢 Building</option>
                                    <option value="SwimmingPool">🏊 Pool</option>
                                    <option value="Umbrella">🏖️ Umbrella</option>
                                </select>
                            </div>

                            <div className="col-12">
                                <label className="form-label text-muted small fw-bold text-uppercase tracking-wider d-block" style={{ fontSize: '0.6rem' }}>
                                    Service Features
                                </label>
                                {currentOffer?.features && currentOffer.features.map((f, i) => (
                                    <div key={i} className="mb-1">
                                        <div className="input-group shadow-sm rounded-4 overflow-hidden" style={{ height: '32px' }}>
                                            <span className="input-group-text bg-white border-0 ps-3 text-warning" style={{ fontSize: '0.7rem' }}>
                                                <Star size={12} fill="currentColor" />
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control border-0 bg-white"
                                                style={{ fontSize: '0.8rem', padding: '2px 10px' }}
                                                placeholder={`Feature ${i + 1}`}
                                                value={f}
                                                onChange={e => handleFeatureChange(i, e.target.value)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="d-flex gap-3 mt-2 pt-2 border-top flex-shrink-0">
                            <button type="submit" className="btn flex-grow-1 rounded-pill py-1.5 fw-bold shadow-lg d-flex align-items-center justify-content-center gap-2 text-white border-0"
                                style={{ background: 'linear-gradient(135deg, #1b4d3e 0%, #2d5a27 100%)', fontSize: '0.8rem' }}>
                                <Save size={16} /> Publish
                            </button>
                            <button type="button" className="btn btn-light rounded-pill px-4 py-1.5 border fw-bold" style={{ fontSize: '0.8rem' }} onClick={onCancel}>
                                Discard
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="col-lg-5 d-flex">
                <CampaignPreview currentOffer={currentOffer} />
            </div>
        </div>
    );
};

// ==================== MAIN COMPONENT ====================
const AdvertisementManager = () => {
    const [landscapingOffers, setLandscapingOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list');
    const [currentOffer, setCurrentOffer] = useState(null);
    const [toast, setToast] = useState(null);

    const loadCampaigns = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/api/campaigns`);
            const data = await response.json();
            
            const mappedData = data.map(campaign => ({
                ...campaign,
                image: campaign.image || campaign.image_url || '',
                beforeImage: campaign.beforeImage || campaign.before_image_url || '',
                campaign_type: campaign.campaign_type || 'landscaping'
            }));
            
            const landscaping = mappedData.filter(c => c.campaign_type === 'landscaping');
            
            setLandscapingOffers(landscaping);
            localStorage.setItem('nursery_landscaping_offers', JSON.stringify(landscaping));
        } catch (error) {
            console.error('Error loading campaigns:', error);
            loadFromLocalStorage();
        } finally {
            setLoading(false);
        }
    };

    const loadFromLocalStorage = () => {
        const savedLandscaping = localStorage.getItem('nursery_landscaping_offers');
        
        if (savedLandscaping) {
            try {
                setLandscapingOffers(JSON.parse(savedLandscaping));
            } catch (e) {
                setLandscapingOffers(defaultLandscapingOffers);
            }
        } else {
            setLandscapingOffers(defaultLandscapingOffers);
        }
    };

    useEffect(() => {
        loadCampaigns();
    }, []);

    const handleAddOffer = () => {
        const newOffer = {
            title: '',
            description: '',
            features: ['', '', ''],
            color: '#2D7D46',
            active: true,
            campaign_type: 'landscaping',
            price: '',
            icon: 'Home',
            location: '',
            image: '',
            beforeImage: ''
        };
        setCurrentOffer(newOffer);
        setView('edit');
    };

    const handleEditOffer = (offer) => {
        setCurrentOffer({ 
            ...offer,
            image: offer.image || offer.image_url || '',
            beforeImage: offer.beforeImage || offer.before_image_url || ''
        });
        setView('edit');
    };

    const handleDeleteOffer = async (id) => {
        if (!window.confirm('Are you sure you want to delete this offer?')) return;

        try {
            const response = await fetch(`${API_BASE}/api/campaigns/${id}`, {
                method: 'DELETE'
            });
            
            const result = await response.json();
            
            if (result.success) {
                setToast({ message: 'Campaign deleted successfully', type: 'success' });
                loadCampaigns();
            } else {
                setToast({ message: 'Error: ' + result.error, type: 'error' });
            }
        } catch (error) {
            console.error('Error deleting campaign:', error);
            setToast({ message: 'Network error. Please try again.', type: 'error' });
        }
    };

    const handleSaveOffer = async (e) => {
        e.preventDefault();
        
        if (!currentOffer.title || !currentOffer.description) {
            setToast({ message: 'Title and Description are required', type: 'error' });
            return;
        }

        try {
            const formData = new FormData();
            
            if (currentOffer.title) formData.append('title', currentOffer.title);
            if (currentOffer.description) formData.append('description', currentOffer.description);
            if (currentOffer.features) {
                const featuresArray = Array.isArray(currentOffer.features) 
                    ? currentOffer.features 
                    : currentOffer.features.split(',').map(f => f.trim()).filter(f => f);
                formData.append('features', JSON.stringify(featuresArray));
            }
            if (currentOffer.color) formData.append('color', currentOffer.color);
            if (currentOffer.campaign_type) formData.append('campaign_type', currentOffer.campaign_type);
            if (currentOffer.location) formData.append('location', currentOffer.location);
            if (currentOffer.price !== undefined && currentOffer.price !== null && currentOffer.price !== '') {
                formData.append('price', currentOffer.price.toString());
            }
            if (currentOffer.icon) formData.append('icon', currentOffer.icon);
            if (currentOffer.active !== undefined) formData.append('active', currentOffer.active);
            
            const imageInput = document.getElementById('campaignImage');
            if (imageInput && imageInput.files && imageInput.files[0]) {
                formData.append('image', imageInput.files[0]);
            } else if (currentOffer.image && typeof currentOffer.image === 'string' && !currentOffer.imageFile) {
                formData.append('existingImageUrl', currentOffer.image);
            }
            
            const beforeInput = document.getElementById('beforeImage');
            if (beforeInput && beforeInput.files && beforeInput.files[0]) {
                formData.append('before_image_url', beforeInput.files[0]);
            } else if (currentOffer.beforeImage && typeof currentOffer.beforeImage === 'string' && !currentOffer.beforeImageFile) {
                formData.append('existingBeforeImageUrl', currentOffer.beforeImage);
            }

            const url = currentOffer.id 
                ? `${API_BASE}/api/campaigns/${currentOffer.id}`
                : `${API_BASE}/api/campaigns`;

            const method = currentOffer.id ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                body: formData
            });

            const result = await response.json();
            
            if (result.success) {
                setToast({ message: 'Campaign saved successfully!', type: 'success' });
                loadCampaigns();
                setView('list');
            } else {
                setToast({ message: 'Error: ' + (result.error || 'Unknown error'), type: 'error' });
            }
        } catch (error) {
            console.error('Error saving campaign:', error);
            setToast({ message: 'Network error. Please try again.', type: 'error' });
        }
    };

    const toggleStatus = async (id) => {
        const offer = landscapingOffers.find(o => o.id === id);
        if (!offer) return;

        try {
            const response = await fetch(`${API_BASE}/api/campaigns/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...offer,
                    active: !offer.active
                })
            });
            
            const result = await response.json();
            
            if (result.success) {
                loadCampaigns();
            }
        } catch (error) {
            console.error('Error toggling status:', error);
            setToast({ message: 'Error updating status', type: 'error' });
        }
    };

    const premiumFont = "'Book Antiqua', 'Baskerville Old Face', 'Times New Roman', serif";

    return (
        <div className="container-fluid py-3 min-vh-100" style={{ background: '#f8fafc', fontFamily: premiumFont }}>
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-success" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading campaigns...</p>
                </div>
            ) : (
                <>
                    <Header onAddOffer={handleAddOffer} />

                    {view === 'list' ? (
                        <CampaignList
                            offers={landscapingOffers}
                            onEdit={handleEditOffer}
                            onDelete={handleDeleteOffer}
                            onToggle={toggleStatus}
                            onAdd={handleAddOffer}
                        />
                    ) : (
                        <CampaignForm                            currentOffer={currentOffer}
                            setCurrentOffer={setCurrentOffer}
                            onSave={handleSaveOffer}
                            onCancel={() => setView('list')}
                        />
                    )}
                </>
            )}

            <style>{`
                .animate-slide-up { animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1); }
                @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
                .tracking-wider { letter-spacing: 0.1em; }
                .tracking-widest { letter-spacing: 0.2em; }
                .shadow-2xl { box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5); }
                .hover-translate:hover { transform: translateY(-5px); }
            `}</style>
        </div>
    );
};

export default AdvertisementManager;
